/**
 * Unified Navigation Configuration
 * Single source of truth for all navigation items across the application
 */

import { 
  Home,
  Store, 
  Workflow,
  Bot,
  BarChart3,
  Settings,
  Users,
  Shield,
  CreditCard,
  User,
  Zap,
  PlusCircle,
  Activity,
  DollarSign,
  ExternalLink,
  Puzzle
} from 'lucide-react';

export interface UnifiedNavigationItem {
  key: string;
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
  roles?: string[];
  badge?: string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline';
  description?: string;
  external?: boolean;
  section: 'main' | 'admin';
}

/**
 * Complete navigation configuration
 * This is the ONLY place where navigation items should be defined
 */
export const UNIFIED_NAVIGATION_CONFIG: UnifiedNavigationItem[] = [
  // ============================================================================
  // MAIN NAVIGATION SECTION
  // ============================================================================
  {
    key: 'dashboard',
    name: 'Dashboard',
    href: '/dashboard',
    icon: Home,
    section: 'main',
    description: 'Your personal dashboard and overview'
  },
  {
    key: 'marketplace',
    name: 'Marketplace',
    href: '/marketplace',
    icon: Store,
    section: 'main',
    description: 'Browse and purchase AI agents'
  },
  {
    key: 'workflow-agents',
    name: 'Workflow Agents',
    href: '/workflow-agents',
    icon: Bot,
    section: 'main',
    badge: 'Discover',
    badgeVariant: 'secondary',
    description: 'Discover workflow-enabled agents'
  },
  {
    key: 'workflows',
    name: 'Workflows',
    href: '/workflows',
    icon: Workflow,
    section: 'main',
    permission: 'workflows.create',
    badge: 'Enhanced',
    badgeVariant: 'secondary',
    description: 'AI workflow builder with templates'
  },
  {
    key: 'my-workflows',
    name: 'My Workflows',
    href: '/my-workflows',
    icon: Activity,
    section: 'main',
    permission: 'pods.view_own',
    badge: 'Active',
    badgeVariant: 'outline',
    description: 'Your created and executed workflows'
  },
  {
    key: 'my-ai-services',
    name: 'My AI Services',
    href: '/my-ai-services',
    icon: Bot,
    section: 'main',
    permission: 'pods.create.subscribed_agent',
    badge: 'Persistent',
    badgeVariant: 'outline',
    description: 'Your persistent AI services'
  },
  {
    key: 'custom-agents',
    name: 'My Agents',
    href: '/custom-agents',
    icon: Bot,
    section: 'main',
    permission: 'agents.create',
    badge: 'Enhanced',
    badgeVariant: 'secondary',
    description: 'Manage agentified workflows, assign sellers, configure credentials'
  },
  {
    key: 'plugins',
    name: 'Plugins',
    href: '/plugins',
    icon: Zap,
    section: 'main',
    permission: 'workflows.create',
    description: 'Manage and configure plugins'
  },
  {
    key: 'mcp-tools',
    name: 'MCP Tools',
    href: '/mcp-tools',
    icon: Settings,
    section: 'main',
    badge: 'AI',
    badgeVariant: 'secondary',
    description: 'AI-powered tool selection and workflow automation'
  },
  {
    key: 'subscription',
    name: 'Subscription',
    href: '/subscription',
    icon: CreditCard,
    section: 'main',
    description: 'Manage your subscription'
  },
  {
    key: 'docs',
    name: 'Docs',
    href: '/docs',
    icon: ExternalLink,
    section: 'main',
    external: false,
    description: 'API documentation and guides'
  },

  // ============================================================================
  // ADMIN NAVIGATION SECTION
  // ============================================================================
  {
    key: 'admin-panel',
    name: 'Admin Panel',
    href: '/admin',
    icon: Shield,
    section: 'admin',
    permission: 'admin.access',
    roles: ['admin', 'super_admin'],
    description: 'System administration'
  },
  {
    key: 'user-management',
    name: 'User Management',
    href: '/admin/users',
    icon: Users,
    section: 'admin',
    permission: 'users.manage',
    roles: ['admin', 'super_admin'],
    description: 'Manage users and permissions'
  },
  {
    key: 'pricing-management',
    name: 'Pricing Management',
    href: '/admin/pricing',
    icon: DollarSign,
    section: 'admin',
    permission: 'admin.access',
    roles: ['super_admin'],
    badge: 'Super Admin',
    badgeVariant: 'destructive',
    description: 'Manage pricing plans and subscriptions'
  },
  {
    key: 'analytics',
    name: 'Analytics',
    href: '/analytics',
    icon: BarChart3,
    section: 'admin',
    permission: 'analytics.view',
    roles: ['admin', 'super_admin'],
    description: 'Performance insights and analytics'
  },
  {
    key: 'executions',
    name: 'Executions',
    href: '/executions',
    icon: Activity,
    section: 'admin',
    permission: 'admin.access',
    roles: ['super_admin'],
    description: 'View all workflow execution history (Super Admin only)'
  }
];

/**
 * Get navigation items filtered by section
 */
export function getNavigationBySection(section: 'main' | 'admin'): UnifiedNavigationItem[] {
  return UNIFIED_NAVIGATION_CONFIG.filter(item => item.section === section);
}

/**
 * Get all main navigation items
 */
export function getMainNavigation(): UnifiedNavigationItem[] {
  return getNavigationBySection('main');
}

/**
 * Get all admin navigation items
 */
export function getAdminNavigation(): UnifiedNavigationItem[] {
  return getNavigationBySection('admin');
}

/**
 * Find a navigation item by key
 */
export function getNavigationItem(key: string): UnifiedNavigationItem | undefined {
  return UNIFIED_NAVIGATION_CONFIG.find(item => item.key === key);
}

/**
 * Check if user has access to a navigation item
 */
export function hasNavigationAccess(
  item: UnifiedNavigationItem,
  userRole: string,
  userPermissions: string[],
  hasPermissionFn: (permission: string) => boolean
): boolean {
  // Check roles if specified
  if (item.roles && item.roles.length > 0) {
    if (!item.roles.includes(userRole)) {
      return false;
    }
  }

  // Check permissions if specified
  if (item.permission) {
    if (!hasPermissionFn(item.permission)) {
      return false;
    }
  }

  return true;
}

/**
 * Filter navigation items based on user role and permissions
 * Completely hide admin sections from basic users
 */
export function getFilteredNavigation(
  userRole: string,
  userPermissions: string[],
  hasPermissionFn: (permission: string) => boolean,
  section?: 'main' | 'admin'
): UnifiedNavigationItem[] {
  let items = section ? getNavigationBySection(section) : UNIFIED_NAVIGATION_CONFIG;
  
  // For basic users, completely filter out admin section items
  if (userRole === 'basic_user') {
    items = items.filter(item => item.section !== 'admin');
  }
  
  // Filter based on permissions and roles
  return items.filter(item => 
    hasNavigationAccess(item, userRole, userPermissions, hasPermissionFn)
  );
}